import type { ReactNode } from 'react';

import {
    Card,
    CardHeader,
    Dropdown,
    Field,
    Input,
    Table,
    TableBody,
    TableCell,
    TableFootNote,
    TableHead,
    TableHeaderCell,
    TableRow,
} from '@/design-system';
import type { PlatformTenant } from '@/entities/tenant';
import {
    MEMBERSHIP_STATUS_LABELS,
    MEMBERSHIP_STATUSES,
    type MembershipStatus,
    type PlatformUser,
    type PlatformUserFilters,
} from '@/entities/user';
import { ROLE_LABELS, ROLES, type Role } from '@/shared/api';
import { formatDayShort } from '@/shared/lib';

import { adminCopy } from '../copy';

/** Bedrijfsrollen; de platformrollen staan niet in `tenant_user`. */
const TENANT_ROLES = ROLES.filter((role) => !role.startsWith('platform_'));

export interface PlatformUsersTableProps {
    users: readonly PlatformUser[];
    filters: PlatformUserFilters;
    onFiltersChange: (filters: PlatformUserFilters) => void;
    tenants: readonly PlatformTenant[];
    onOpen: (user: PlatformUser) => void;
    footer?: ReactNode;
}

/**
 * Wie werkt waar - het cross-tenant beeld.
 *
 * Eén rij per **persoon**. Dit is het enige scherm waar dat mag: binnen een
 * keuringsbedrijf is het bedrijfsgevoelig bij welke andere bedrijven iemand nog
 * meer werkt. Een rij opent de persoon met al zijn lidmaatschappen.
 */
export function PlatformUsersTable({
    users,
    filters,
    onFiltersChange,
    tenants,
    onOpen,
    footer,
}: PlatformUsersTableProps) {
    return (
        <Card clip as="section" aria-labelledby="platform-users-title">
            <CardHeader
                title={<span id="platform-users-title">{adminCopy.usersTitle}</span>}
                description={adminCopy.usersIntro}
            />

            <div className="flex flex-wrap items-end gap-2 border-b border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-3">
                <Field label={adminCopy.usersTenant} size="sm" className="min-w-[180px]">
                    <Dropdown
                        value={filters.tenantUuid ?? ''}
                        options={[
                            { value: '', label: adminCopy.usersTenantAll },
                            ...tenants.map((tenant) => ({
                                value: tenant.uuid,
                                label: tenant.name,
                            })),
                        ]}
                        onChange={(tenantUuid) =>
                            onFiltersChange({ ...filters, tenantUuid, page: 1 })
                        }
                    />
                </Field>

                <Field label={adminCopy.usersRole} size="sm" className="min-w-[150px]">
                    <Dropdown<Role | ''>
                        value={filters.role ?? ''}
                        options={[
                            { value: '', label: adminCopy.usersRoleAll },
                            ...TENANT_ROLES.map((role) => ({
                                value: role,
                                label: ROLE_LABELS[role],
                            })),
                        ]}
                        onChange={(role) => onFiltersChange({ ...filters, role, page: 1 })}
                    />
                </Field>

                <Field label={adminCopy.usersStatus} size="sm" className="min-w-[150px]">
                    <Dropdown<MembershipStatus | ''>
                        value={filters.status ?? ''}
                        options={[
                            { value: '', label: adminCopy.usersStatusAll },
                            ...MEMBERSHIP_STATUSES.map((status) => ({
                                value: status,
                                label: MEMBERSHIP_STATUS_LABELS[status],
                            })),
                        ]}
                        onChange={(status) => onFiltersChange({ ...filters, status, page: 1 })}
                    />
                </Field>

                <Field label={adminCopy.usersSearch} size="sm" className="min-w-[180px] flex-1">
                    <Input
                        type="search"
                        icon="search"
                        value={filters.search ?? ''}
                        placeholder={adminCopy.usersSearchPlaceholder}
                        onChange={(event) =>
                            onFiltersChange({ ...filters, search: event.target.value, page: 1 })
                        }
                    />
                </Field>
            </div>

            <Table
                columns="minmax(0,1.1fr) minmax(0,1.2fr) minmax(0,1.3fr) 116px"
                minWidth={760}
                label={adminCopy.usersTitle}
            >
                <TableHead>
                    <TableHeaderCell>{adminCopy.columnName}</TableHeaderCell>
                    <TableHeaderCell>{adminCopy.columnEmail}</TableHeaderCell>
                    <TableHeaderCell>{adminCopy.columnCompanies}</TableHeaderCell>
                    <TableHeaderCell align="end">{adminCopy.columnLastLogin}</TableHeaderCell>
                </TableHead>
                <TableBody>
                    {users.map((user) => (
                        <TableRow key={user.uuid} onSelect={() => onOpen(user)}>
                            <TableCell emphasis truncate>
                                {user.name}
                            </TableCell>
                            <TableCell muted truncate>
                                {user.email}
                            </TableCell>
                            <TableCell muted truncate>
                                {user.isPlatformAdmin && user.memberships.length === 0
                                    ? adminCopy.userPlatformAdmin
                                    : user.memberships
                                          .map((membership) => membership.tenantName)
                                          .join(', ') || adminCopy.unknown}
                            </TableCell>
                            <TableCell mono align="end" muted>
                                {user.lastLoginAt
                                    ? formatDayShort(user.lastLoginAt)
                                    : adminCopy.unknown}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {users.length === 0 ? <TableFootNote>{adminCopy.usersEmpty}</TableFootNote> : null}
            {footer}
        </Card>
    );
}
